import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import regexpPatterns from '@/shared/constants/regexpPatterns';
import Customer from '@/shared/types/customer.type';

type Args = {
  customer: Customer;
  selectedCustomerSnapshot: Customer;
  isBillingSameAsDelivery: boolean;
  shouldResetForm: boolean;
};

const useCustomerFormValidation = ({
  customer,
  selectedCustomerSnapshot,
  isBillingSameAsDelivery,
  shouldResetForm,
}: Args) => {
  const { t } = useTranslation(['customers', 'common']);

  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const onValidate = (fieldName: string, useSnapshotValue = false) => {
    const validationConfig: {
      [key: string]: {
        required: boolean;
        pattern?: RegExp;
        errorMessage: string;
        errorPatternMessage?: string;
      };
    } = {
      first_name: {
        required: true,
        errorMessage: 'customers:first_name_required',
      },
      last_name: {
        required: true,
        errorMessage: 'customers:last_name_required',
      },
      email_address: {
        required: true,
        pattern: regexpPatterns.emailValidator,
        errorMessage: 'customers:email_address_required',
        errorPatternMessage: 'customers:invalid_email_address',
      },
      phone: {
        required: true,
        pattern: regexpPatterns.phoneNumberValidator,
        errorMessage: 'customers:phone_required',
        errorPatternMessage: 'customers:invalid_phone',
      },
      address: {
        required: true,
        errorMessage: 'customers:address_required',
      },
      city: {
        required: true,
        errorMessage: 'customers:city_required',
      },
      country: {
        required: true,
        errorMessage: 'customers:country_required',
      },
      state: {
        required: true,
        errorMessage: 'customers:state_required',
      },
      postcode: {
        required: true,
        errorMessage: 'customers:postcode_required',
      },
    };

    const errors: Record<string, string> = { ...formErrors };

    const indexKey = fieldName as keyof Customer;
    const valueToValidate = useSnapshotValue
      ? selectedCustomerSnapshot[indexKey]
      : customer[indexKey];

    const baseFieldName = fieldName.split('_').slice(1).join('_');
    const fieldConfig = validationConfig[baseFieldName];

    if (fieldConfig) {
      if (fieldConfig.required && !valueToValidate) {
        errors[indexKey] = t(fieldConfig.errorMessage);
      } else if (
        fieldConfig.pattern &&
        !fieldConfig.pattern.test(valueToValidate)
      ) {
        const msg = fieldConfig.errorPatternMessage || fieldConfig.errorMessage;
        errors[indexKey] = t(msg);
      } else {
        errors[indexKey] = '';
      }
    }

    setFormErrors(errors);
    return errors;
  };

  const validateAllFields = () => {
    const errors: Record<string, string> = {};

    const fieldsToValidateBilling = [
      'billing_first_name',
      'billing_last_name',
      'billing_email_address',
      'billing_phone',
      'billing_address',
      'billing_city',
      'billing_country',
      'billing_state',
      'billing_postcode',
    ];

    const fieldsToValidateDelivery = [
      'delivery_first_name',
      'delivery_last_name',
      'delivery_email_address',
      'delivery_phone',
      'delivery_address',
      'delivery_city',
      'delivery_country',
      'delivery_state',
      'delivery_postcode',
    ];

    const fieldsToValidate = isBillingSameAsDelivery
      ? [...fieldsToValidateBilling]
      : [...fieldsToValidateBilling, ...fieldsToValidateDelivery];

    fieldsToValidate.forEach((fieldName) => {
      const fieldErrors = onValidate(fieldName);
      errors[fieldName] = fieldErrors[fieldName];
    });

    setFormErrors(errors);
    return Object.entries(errors).some(([, value]) => value);
  };

  useEffect(() => {
    shouldResetForm && setFormErrors({});
  }, [shouldResetForm]);

  return {
    formErrors,
    onValidate,
    validateAllFields,
    setFormErrors,
  };
};

export default useCustomerFormValidation;
